import {
  ShareScreenBase,
  SKIP_WAITLIST_TITLE,
  skipWaitlistSuccessMessage,
} from "./share-screen-base"
import { getDeeplinkUrl } from "~/common/platform"
import { getMetaContent } from "~/common/get-meta-content"
import { brandAppName } from "~/common/brand"
import appPreviewImage from "~/images/landing/app-preview-image"
import { useDocumentTitleDescription } from "~/common/use-document-title"
import { AppText } from "~/ui/app-text"
import { useInviteFriendPhoneSubmission } from "~/hooks/use-phone-submission"
import { useInviteFriendEmailSubmission } from "~/hooks/use-email-submission"
import { useCollectEmail } from "~/hooks/use-collect-email"
import { useCallback } from "react"
import { EVENT_IDS, SCREENS, SHARE_PAGES } from "~/common/analytics"
import { getInviterData } from "~/share/share-embedded-data"
import { useScreenTracking } from "~/hooks/use-screen-tracking"

export const InviteScreen = (): JSX.Element => {
  const appEnvironment = getMetaContent<string>("APP_ENVIRONMENT")
  const deeplinkUrl = getDeeplinkUrl(appEnvironment, "home")
  const collectEmail = useCollectEmail()
  const phoneSubmission = useInviteFriendPhoneSubmission()
  const emailSubmission = useInviteFriendEmailSubmission()
  useDocumentTitleDescription(
    `You're invited to ${brandAppName()}`,
    `Join ${brandAppName()}, the group chats hosted by your favorite podcasters, reality stars, athletes & more.`
  )
  useScreenTracking(SCREENS.Share, {
    share_page: SHARE_PAGES.Invite,
    deeplink_url: deeplinkUrl,
  })

  const onJoinPress = useCallback(async () => {
    const successMessage = skipWaitlistSuccessMessage(collectEmail)
    const successTitle = SKIP_WAITLIST_TITLE
    const inviterData = getInviterData()

    const trackingData = {
      unique_id: EVENT_IDS.SHARE_SCREEN_SKIP_WAITLIST,
      share_page: SHARE_PAGES.Invite,
      inviter_id: inviterData?.id,
      inviter_username: inviterData?.username,
      deeplink_url: deeplinkUrl,
    }

    if (collectEmail) {
      await emailSubmission.submitEmail({ successMessage, successTitle, trackingData })
    } else {
      await phoneSubmission.submitPhoneNumber({ successMessage, successTitle, trackingData })
    }
  }, [collectEmail, deeplinkUrl, emailSubmission, phoneSubmission])

  const inputProps = collectEmail
    ? {
        inputMode: "email" as const,
        emailValue: emailSubmission.emailValue,
        setEmailValue: emailSubmission.setEmailValue,
      }
    : {
        inputMode: "phone" as const,
        phoneValue: phoneSubmission.phoneValue,
        setPhoneValue: phoneSubmission.setPhoneValue,
      }

  return (
    <ShareScreenBase
      deeplinkUrl={deeplinkUrl}
      className="justify-center"
      onJoinPress={onJoinPress}
      {...inputProps}
      renderContent={() => (
        <div className="flex flex-col items-center justify-center md:my-[-24px]">
          <AppText
            variant="body2-medium"
            className="max-w-[400px] px-4 py-2 text-center text-base-white md:max-w-[600px] md:text-18"
          >
            Epic group chats hosted by your favorite podcasters, politicos, reality stars, athletes,
            comedians & more.
          </AppText>
          <img src={appPreviewImage.src} alt="App Preview" className="w-full md:max-w-[800px]" />
        </div>
      )}
    />
  )
}

export default InviteScreen
